import * as React from 'react';
import { Multiselect } from 'react-widgets';
import { BaseFieldProps, Field, GenericField, WrappedFieldProps } from 'redux-form';
import { FieldWrapper, FieldWrapperConfig } from './FieldWrapper';
import { ReactNode } from 'react';
import classNames from 'classnames';
import styles from '../assets/wrapper-input.module.scss';

import { groupFields } from '../utils/groupFields';

interface DropdownComponentProps {
    data: AnyObject[];
    disabled?: boolean;
    textField?: string;
    valueField?: string;
    busy?: boolean;
    firstItem?: { value: 'string'; label: 'string' } | boolean;
    filter?: false | 'startsWith' | 'endsWith' | 'contains' | ((dataItem: any, searchTerm: string) => boolean);
    wrapperConfig?: FieldWrapperConfig;
    children?: ReactNode;
}

type InputProps = DropdownComponentProps & WrappedFieldProps;

/**
 * InputDropdownComponent which renders wrapped input dropdown list field with label and error
 *
 * @constructor
 */
const InputDropdownComponent: React.FunctionComponent<InputProps> = (props: InputProps) => {
    let { items, firstItem }: any = groupFields(props.data);
    const [first, setFirstItem] = React.useState<any>(firstItem);
    React.useEffect(() => {
        if (items.length && !first) {
            setFirstItem(firstItem);
        }
    }, [items]); // eslint-disable-line

    const onChange = (value: any) => {
        if (value[0] && value[0].value === 'all') {
            value = [];
            firstItem = false;
            items.splice(0, 1);
            setFirstItem('no');
            items.forEach((it: { value: number; label: string }) => {
                value.push({ value: it.value, label: it.label });
            });
            props.input.onChange(value);
        } else {
            setFirstItem(false);
            props.input.onChange(value);
        }
    };
    if (first && first !== 'no' && !props.input.value.length) {
        items.unshift(first);
    }

    return (
        <FieldWrapper {...props.wrapperConfig} name={props.input.name} meta={props.meta}>
            <Multiselect
                filter={props.filter}
                busy={props.busy}
                data={items}
                disabled={props.disabled}
                textField={props.textField}
                containerClassName={classNames(styles.multiSelectContainer)}
                defaultValue={[]}
                valueField={props.valueField}
                onChange={onChange}
                value={props.input.value}
            />
            {props.children}
        </FieldWrapper>
    );
};

InputDropdownComponent.defaultProps = {
    textField: 'name',
    valueField: 'value',
    busy: false,
    firstItem: false,
    filter: 'contains',
};

const FieldDropdownComponent = Field as new () => GenericField<DropdownComponentProps>;

type FieldProps = BaseFieldProps<DropdownComponentProps> & DropdownComponentProps;

/**
 * FieldDropdown component which extends GenericField from redux form and renders DropdownList input field
 * (InputDropdownComponent) with all needed props
 *
 * @param props
 * @constructor
 */
export const FieldMultiDropdown: React.FunctionComponent<FieldProps> = (props: FieldProps) => (
    <FieldDropdownComponent {...props} component={InputDropdownComponent} />
);
